import { orderBy } from 'lodash';
import {
  Dimension,
  MeasurementMetric,
  Query,
  QueryResultRow,
  QueryWithConnectionAndMeasurement,
  QueryWithMeasurement,
  Rollup,
  RollupFunction,
  WindowFunction
} from './query.types';
import { getMeasurementMetric } from './measurements';
import { isEnumItem } from './enumUtils';

/**
 * Default number of rows whose time series are rendered when `viz.limit` is not set.
 */
export const DEFAULT_VIZ_LIMIT = 100;

/**
 * Available standard rollup functions for metric aggregation
 */
export const ROLLUP_OPTIONS: { value: RollupFunction; label: string }[] = [
  { value: 'min', label: 'Min' },
  { value: 'avg', label: 'Average' },
  { value: 'max', label: 'Max' },
  { value: 'last', label: 'Last' },
  { value: 'sum', label: 'Total' },
  { value: 'p95', label: 'p95' }
];

/**
 * A subset of rollup options appropriate for enum values (they return valid values)
 */
export const ENUM_ROLLUP_OPTIONS: { value: RollupFunction; label: string }[] = [
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
  { value: 'last', label: 'Last' }
];

/**
 * Get the default rollup function for a metric based on its unit type.
 *
 * Different metric types have different semantics for aggregation:
 * - 'health': Use 'min' (lower = worse: 100=critical, 200=warning, 300=healthy)
 * - 'http_status_code': Use 'max' (higher = worse: 5xx > 4xx > 3xx > 2xx)
 * - 'dns_code': Use 'max' (higher codes are generally errors)
 * - default: Use 'avg'
 */
export function getDefaultRollupFn(metricInfo: MeasurementMetric | null | undefined): RollupFunction {
  if (isEnumItem(metricInfo)) {
    return 'last';
  }

  if (!metricInfo?.Unit) {
    return 'avg';
  }

  const unit = metricInfo.Unit.split(':')[0];

  switch (unit) {
    case 'health':
      return 'min';
    case 'http_status_code':
    case 'dns_code':
      return 'max';
    case 'timestamp':
      return 'last';
    default:
      return 'avg';
  }
}

export function getWindowFn(
  metricInfo: MeasurementMetric | null | undefined,
  rollupFn?: RollupFunction
): WindowFunction {
  if (metricInfo) {
    if (metricInfo.Window) {
      return metricInfo.Window;
    }
    // For SQL-compatible functions (min, max, avg, sum), use directly
    // For post-processing functions (p95, etc.), use 'avg' as SQL base
    const resolvedRollupFn = rollupFn || getDefaultRollupFn(metricInfo);
    if (['avg', 'min', 'max', 'sum'].includes(resolvedRollupFn)) {
      return resolvedRollupFn as WindowFunction;
    }
  }
  return 'avg';
}

export function getQueryDuration(query: Query): number {
  if (query.time.lookback) {
    return query.time.lookback;
  }
  return query.time.end - query.time.start;
}

export function getIsFastData(query: QueryWithConnectionAndMeasurement): boolean | null {
  const duration = getQueryDuration(query);

  if (!query.window) {
    return false;
  }

  const hasSmallWindowOrDuration =
    (query.window && query?.window.size > 0 && query?.window.size < 3600) || duration <= 60 * 60 * 24;
  if (hasSmallWindowOrDuration) {
    return false;
  }

  if (!query.measurementObj?.storage.SupportsFastData) {
    return false;
  }

  if (query.window && query.window.size >= 3600) {
    return true;
  }
  // doing this now until we properly calculate fast data retention
  return null;
}

export function getDimensions(query: Query): Dimension[] {
  return query.dimensions.length === 0 ? [{ name: 'name' }] : query.dimensions;
}

export function getRollupKey(rollup: Rollup): string {
  return `${rollup.fn}_${rollup.metric}`;
}

/**
 * Sort result rows by relevance, using the same precedence applied server-side:
 * explicit `query.sort` first, then aggregate metrics, then rollups. Pass
 * `isAggregate` from `isAggregateQuery` so aggregate queries sort by metric.
 */
export function sortResultRows(
  query: Pick<Query, 'sort' | 'metrics' | 'rollups'>,
  rows: QueryResultRow[],
  { isAggregate = false }: { isAggregate?: boolean } = {}
): QueryResultRow[] {
  if (query.sort?.length) {
    return orderBy(
      rows,
      query.sort.map((s) => (row: QueryResultRow) => row.rollups[s.rollup] ?? 0),
      query.sort.map((s) => s.order ?? 'desc')
    );
  }

  if (isAggregate) {
    return orderBy(
      rows,
      query.metrics.map((m) => (row: QueryResultRow) => row.rollups[m.name] ?? 0),
      Array(query.metrics.length).fill('desc')
    );
  }

  const rollups = query.rollups ?? [];
  if (!rollups.length) {
    // No sort and no rollups to rank by — preserve the incoming order.
    return rows;
  }

  return orderBy(
    rows,
    rollups.map((rollup) => {
      const key = getRollupKey(rollup);
      return (row: QueryResultRow) => row.rollups[key] ?? 0;
    }),
    Array(rollups.length).fill('desc')
  );
}

export function isAggregateQuery(query: Pick<QueryWithMeasurement, 'measurementObj' | 'metrics'>) {
  const { measurementObj } = query;
  return !!getMeasurementMetric(measurementObj, query.metrics[0].name)?.Aggregate;
}

export function isSrcDim(dimensionName: string): boolean {
  return (
    dimensionName.startsWith('src_') ||
    dimensionName.endsWith('_src') ||
    dimensionName.includes('_src_') ||
    dimensionName.startsWith('input_') ||
    dimensionName.includes('_input_') ||
    dimensionName.endsWith('_in')
  );
}

export function isDstDim(dimensionName: string): boolean {
  return (
    dimensionName.startsWith('dst_') ||
    dimensionName.endsWith('_dst') ||
    dimensionName.includes('_dst_') ||
    dimensionName.startsWith('output_') ||
    dimensionName.includes('_output_') ||
    dimensionName.endsWith('_out')
  );
}

export function isDirectionalDim(dimensionName: string): boolean {
  const hasSrc = isSrcDim(dimensionName);
  const hasDst = isDstDim(dimensionName);
  return hasSrc || hasDst;
}

/**
 * Get a display label for a dimension that includes the src/dst prefix when applicable.
 *
 * For dimensions like src_agent_asn or dst_agent_ip, the base Label in the measurement
 * definition is "Agent ASN" or "Agent IP" (without prefix) so the grouped dimension
 * selector UI can show SRC/DST toggle buttons. This function adds the prefix back
 * for contexts where we need to clearly distinguish which direction was selected
 * (e.g., legend headers, sidebar selected dimensions list).
 *
 * @param dimensionName - The dimension name (e.g., 'src_agent_asn', 'dst_agent_ip')
 * @param baseLabel - The base label from the dimension definition (e.g., 'Agent ASN')
 * @returns Display label with prefix if applicable (e.g., 'Src Agent ASN')
 */
export function getDimensionDisplayLabel(dimensionName: string, baseLabel: string): string {
  if (isSrcDim(dimensionName)) {
    return `Source ${baseLabel}`;
  }
  if (isDstDim(dimensionName)) {
    return `Destination ${baseLabel}`;
  }
  return baseLabel;
}
